import { useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, User, AlertTriangle } from "lucide-react";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useBlogPost } from "@/hooks/useBlog";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

import { renderBlogContent } from "@/lib/blogMarkdown";
import AdBanner from "@/components/blog/AdBanner";
import AffiliateWidget from "@/components/blog/AffiliateWidget";
import GoogleAdSense from "@/components/blog/GoogleAdSense";

import StickyShareBar from "@/components/blog/StickyShareBar";
import HeaderShareIcons from "@/components/blog/HeaderShareIcons";
import RelatedProducts from "@/components/blog/RelatedProducts";
import SalonCTA from "@/components/SalonCTA";

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isAdminPreview = searchParams.get("preview") === "true" || searchParams.get("adminPreview") === "true";
  const { data: post, isLoading } = useBlogPost(slug || "", isAdminPreview);

  useEffect(() => {
    if (!post || isAdminPreview) return;
    const key = `blog-viewed-${post.slug}`;
    if (typeof window === "undefined" || sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    supabase.rpc("increment_blog_view" as any, { _slug: post.slug }).then(() => {});
  }, [post, isAdminPreview]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Ticker />
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading…</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Ticker />
        <Navbar />
        <div className="flex-1 flex items-center justify-center flex-col gap-4 py-20">
          <p className="text-muted-foreground">Blog post not found</p>
          <Link to="/blog" className="text-primary hover:underline">Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const wordCount = post.content.split(/\s+/).length;
  const readTime = Math.max(1, Math.round(wordCount / 220));

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={post.title}
        description={post.excerpt}
        url={`/blog/${post.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          author: { "@type": "Person", name: post.author },
          datePublished: post.created_at,
          publisher: {
            "@type": "Organization",
            name: "Plymhair & Extensions",
          },
          mainEntityOfPage: `https://www.plymhair.co.uk/blog/${post.slug}`,
        }}
      />
      <Ticker />
      <Navbar />

      <article className="flex-1">
        {/* Draft banner */}
        {post && !post.published && (
          <div className="bg-yellow-50 border-b border-yellow-200 py-3">
            <div className="container max-w-3xl flex items-center gap-2 text-yellow-800 text-sm font-body">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">Draft Preview</span> — This post is not published yet.
            </div>
          </div>
        )}
        {/* Header */}
        <section className="bg-foreground text-primary-foreground py-20 md:py-28">
          <div className="container max-w-3xl">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-primary-foreground/50 hover:text-primary text-sm font-body mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            <h1 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-5">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-primary-foreground/50 text-sm font-body">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {format(new Date(post.created_at), "d MMMM yyyy")}
              </span>
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span>{readTime} min read</span>
              <HeaderShareIcons
                url={typeof window !== "undefined" ? window.location.href : `https://www.plymhair.co.uk/blog/${post.slug}`}
                title={post.title}
                slug={post.slug}
              />
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 md:py-16">
          <div className="container max-w-3xl">
            <AdBanner placement="top" />

            {renderBlogContent(post.content)}

            {post.category === "salon" ? (
              <SalonCTA topic={post.title} />
            ) : (
              <SalonCTA variant="compact" />
            )}

            <GoogleAdSense />

            <RelatedProducts postTitle={post.title} postExcerpt={post.excerpt} />

            <AffiliateWidget />

            <AdBanner placement="bottom" />

            {/* CTA */}
            {post.category !== "salon" && (
              <div className="mt-12 p-8 bg-primary/5 rounded-2xl border border-primary/10 text-center">
                <h3 className="font-display text-xl font-semibold mb-2">Ready to find your perfect look?</h3>
                <p className="font-body text-muted-foreground mb-4">Browse our premium collection of wigs, extensions and braids.</p>
                <Link
                  to="/shop"
                  className="inline-block bg-primary text-primary-foreground font-body text-sm font-semibold tracking-wider uppercase px-8 py-3 rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Shop Now
                </Link>
              </div>
            )}
          </div>
        </section>
      </article>

      <StickyShareBar
        url={typeof window !== "undefined" ? window.location.href : `https://www.plymhair.co.uk/blog/${post.slug}`}
        title={post.title}
        image={post.cover_image || undefined}
        slug={post.slug}
      />

      <Footer />
    </div>
  );
};

export default BlogPostPage;
